
import React, { useState, useEffect } from 'react';
import { supabase, isSupabaseConfigured } from '../../lib/supabase';
import { Sample, AnalysisCatalogItem, AnalysisResult } from '../../types';
import { FlaskConical, Save, CheckCircle } from 'lucide-react';

interface ResultValidationModalProps {
  sample: Sample;
  catalogItems: AnalysisCatalogItem[];
  onClose: () => void;
  onSuccess: () => void;
}

const ResultValidationModal: React.FC<ResultValidationModalProps> = ({ sample, catalogItems, onClose, onSuccess }) => {
  const [resultsData, setResultsData] = useState<Record<string, { result: string, date: string }>>({});
  const [displayAnalyses, setDisplayAnalyses] = useState<string[]>([]);

  useEffect(() => {
    // 1. Expand Panels logic:
    // If an analysis in sample.analyses is a Panel, we must show its sub-analyses inputs instead of the panel itself.
    const expanded: string[] = [];
    (sample.analyses || []).forEach(analysisName => {
        // We trim to match safely
        const item = catalogItems.find(c => c.name.trim() === analysisName.trim());
        
        if (item && item.sub_analyses && item.sub_analyses.length > 0) {
            // It is a panel -> find children and add them
            const children = catalogItems.filter(c => item.sub_analyses?.includes(c.id));
            if (children.length > 0) {
                 children.forEach(c => expanded.push(c.name));
            } else {
                 // Fallback if children not found (e.g. catalog inconsistency), show the panel itself
                 expanded.push(analysisName);
            }
        } else {
            // Regular analysis -> add as is
            expanded.push(analysisName);
        }
    });
    // Remove duplicates
    const uniqueAnalyses = Array.from(new Set(expanded));
    setDisplayAnalyses(uniqueAnalyses);

    // 2. Initialize Data
    const initialData: Record<string, { result: string, date: string }> = {};
    
    // Index existing results
    const existingResults: Record<string, AnalysisResult> = {};
    if (sample.results && Array.isArray(sample.results)) {
      sample.results.forEach(r => existingResults[r.analysis] = r);
    }

    uniqueAnalyses.forEach(analysis => {
        const existing = existingResults[analysis];
        initialData[analysis] = { 
            result: existing ? existing.result : '', 
            date: existing ? existing.date : new Date().toISOString().split('T')[0] 
        };
    });
    setResultsData(initialData);
  }, [sample, catalogItems]);

  const handleResultChange = (analysis: string, field: 'result' | 'date', value: string) => {
      setResultsData(prev => ({
          ...prev,
          [analysis]: { ...prev[analysis], [field]: value }
      }));
  };

  const getFormattedResults = () => {
    return Object.entries(resultsData).map(([analysis, data]) => {
        const d = data as { result: string, date: string };
        return {
            analysis,
            result: d.result,
            date: d.date
        };
    });
  };

  const savePartialResults = async () => {
      const formattedResults = getFormattedResults();

      if (isSupabaseConfigured()) {
          const { error } = await supabase.from('samples')
            .update({ results: formattedResults })
            .eq('id', sample.id);
          
          if (!error) onSuccess();
      } else {
          onSuccess();
      }
  };

  const confirmValidation = async () => {
      const formattedResults = getFormattedResults();

      if (formattedResults.some(r => !r.result)) {
          alert("Veuillez remplir tous les résultats.");
          return;
      }

      if (isSupabaseConfigured()) {
          const { error } = await supabase.from('samples')
            .update({ status: 'validated', results: formattedResults })
            .eq('id', sample.id);
          
          if (!error) onSuccess();
      } else {
          onSuccess();
      }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-xl shadow-xl w-full max-w-lg overflow-hidden flex flex-col max-h-[90vh]">
            <div className="px-6 py-4 border-b border-slate-200 bg-slate-50 flex justify-between items-center">
                <h3 className="font-bold text-lg text-slate-800">Saisie des Résultats</h3>
                <button onClick={onClose} className="text-slate-400 hover:text-slate-600">&times;</button>
            </div>
            <div className="p-6 overflow-y-auto">
                 <p className="text-sm text-slate-600 mb-6">
                    Veuillez saisir les résultats pour les analyses en cours.
                </p>
                
                <div className="space-y-6">
                    {displayAnalyses.map(analysisName => {
                        const catalogItem = catalogItems.find(c => c.name === analysisName);
                        // Determine Input Type
                        let isChoice = catalogItem?.result_type === 'choice' || (catalogItem?.possible_results && catalogItem.possible_results.length > 0);
                        let isNumeric = catalogItem?.result_type === 'numeric';
                        
                        return (
                            <div key={analysisName} className="p-4 bg-slate-50 rounded-lg border border-slate-200">
                                <h4 className="font-bold text-slate-800 mb-3 flex items-center gap-2">
                                    <FlaskConical size={16} className="text-indigo-600"/>
                                    {analysisName}
                                </h4>
                                <div className="grid md:grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-xs font-bold text-slate-700 mb-1">
                                            Résultat {catalogItem?.unit ? `(${catalogItem.unit})` : ''}
                                        </label>
                                        {isChoice ? (
                                           <select 
                                               value={resultsData[analysisName]?.result || ''} 
                                               onChange={(e) => handleResultChange(analysisName, 'result', e.target.value)}
                                               className="w-full p-2 border border-slate-300 rounded text-sm bg-white"
                                           >
                                               <option value="">Sélectionner...</option>
                                               {catalogItem?.possible_results?.map(opt => (
                                                   <option key={opt} value={opt}>{opt}</option>
                                               ))}
                                           </select>
                                        ) : (
                                            <input 
                                                type={isNumeric ? "number" : "text"}
                                                step="any"
                                                value={resultsData[analysisName]?.result || ''}
                                                onChange={(e) => handleResultChange(analysisName, 'result', e.target.value)}
                                                className="w-full p-2 border border-slate-300 rounded text-sm bg-white"
                                                placeholder={isNumeric ? "0.00" : "Résultat..."}
                                            />
                                        )}
                                        {isNumeric && (catalogItem?.min_ref !== undefined || catalogItem?.max_ref !== undefined) && (
                                            <div className="mt-1 text-[10px] text-slate-500 flex justify-between">
                                                <span>Ref: {catalogItem.min_ref ?? '...'} - {catalogItem.max_ref ?? '...'}</span>
                                            </div>
                                        )}
                                    </div>
                                    <div>
                                        <label className="block text-xs font-bold text-slate-700 mb-1">Date</label>
                                        <input 
                                            type="date"
                                            value={resultsData[analysisName]?.date || ''}
                                            onChange={(e) => handleResultChange(analysisName, 'date', e.target.value)}
                                            className="w-full p-2 border border-slate-300 rounded text-sm bg-white"
                                        />
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>

                <div className="mt-6 flex justify-end gap-3 pt-4 border-t border-slate-100">
                    <button onClick={savePartialResults} className="px-4 py-2 text-slate-600 hover:bg-slate-100 rounded-lg text-sm font-medium flex items-center gap-2">
                        <Save size={16} /> Enregistrer (Brouillon)
                    </button>
                    <button onClick={confirmValidation} className="px-4 py-2 bg-emerald-600 text-white rounded-lg text-sm font-medium hover:bg-emerald-700 shadow-sm flex items-center gap-2">
                        <CheckCircle size={16} /> Valider Terminé
                    </button>
                </div>
            </div>
        </div>
    </div>
  );
};

export default ResultValidationModal;
